import { CommonModule, Location, ViewportScroller } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AssessmentsService } from '../../services/assessments.service';
import { NhptData, NhptDataSet, initDataNHPT } from '../../services/model';
import { getDateIsoString, getDateIsoStringPlus1Minute } from '../../services/util.functions';
import { AssessmentBaseComponent } from '../assessment-base/assessment-base.component';
import { DynamicTableInputNhptComponent } from './dynamic-table-input-nhpt/dynamic-table-input-nhpt.component';
import { DocumentReference } from '@angular/fire/firestore';
import { NavigationComponent } from '../../components/navigation/navigation.component';

@Component({
  selector: 'app-nhpt',
  standalone: true,
  imports: [
    DynamicTableInputNhptComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    RouterModule,
    MatIconModule,
    MatTooltipModule,
    NavigationComponent,
  ],
  templateUrl: './nhpt.component.html',
  styleUrl: './nhpt.component.scss',
})
export class NhptComponent extends AssessmentBaseComponent implements OnInit {
  location: Location = inject(Location);
  @Input() assessmentId: string | undefined;
  protected readonly anleitung =
    'Beim NHPT werden 9 Stäbchen in 9 Löcher gesteckt und wieder zurückgelegt.\n\n' +
    'Messung: Zeitmessung in Sekunden\n\n' +
    'Bei Abbruch nach 60 s (optional): Summe der eingesteckten Dübel und (falls vorhanden) der zurückgelegten Dübel\n\n';
  private assessmentService: AssessmentsService = inject(AssessmentsService);

  private scroller: ViewportScroller = inject(ViewportScroller);
  private fb: FormBuilder = inject(FormBuilder);
  assessmentData: WritableSignal<NhptData | undefined> = signal<NhptData | undefined>(undefined);
  assessmentDataNew: NhptData = JSON.parse(JSON.stringify(initDataNHPT));
  hasError = signal({
    nhpt: false,
  });

  form: FormGroup = this.fb.group({
    assessmentTime: [getDateIsoStringPlus1Minute(), Validators.required],
    note: [''],
  });

  onNhptData(data: { data: Array<NhptDataSet>; note: string; hasError: boolean }) {
    console.log('data', data);

    this.assessmentDataNew.nhpt.data = data.data;
    this.assessmentDataNew.nhpt.note = data.note;
    this.hasError.update(current => ({
      ...current,
      nhpt: data.hasError,
    }));
  }

  ngOnInit(): void {
    this.scroller.scrollToPosition([0, 0]);

    if (this.assessmentId) {
      this.assessmentService.getAssessment(this.assessmentId).then(data => {
        const nhptData: NhptData = data.data as NhptData;
        // add manual
        nhptData.nhpt.manual = initDataNHPT.nhpt.manual;

        this.assessmentData.set(nhptData);
        const date = getDateIsoString(new Date(data.dateIso));
        this.form.controls['assessmentTime'].patchValue(date);

        let note = data.note;
        const hasNewNote = note && note.length > 0;
        if (!hasNewNote) {
          // try migration
          note = nhptData.nhpt.note;
          nhptData.nhpt.note = '';
        }

        this.form.controls['note'].patchValue(note);

        this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
      });
    } else {
      this.assessmentData.set(initDataNHPT);
      this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
    }
  }
  onSave() {
    if (this.hasError().nhpt) {
      return;
    }
    if (this.assessmentId) {
      this.assessmentService
        .updateAssessment(this.assessmentId, {
          type: 'NHPT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then(res => {
          this.router.navigate(['../../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'NHPT', dId: this.assessmentId },
          });
        });
    } else {
      this.assessmentService
        .createAssessment({
          type: 'NHPT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then((docRef: DocumentReference) => {
          this.router.navigate(['../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'NHPT', dId: docRef.id },
          });
        });
    }
  }

  onNavigateBack() {
    if (this.assessmentId) {
      // this.router.navigate(['../../../reports/assessments'], { relativeTo: this.route });
      this.location.back();
    } else {
      // this.router.navigate(['../../reports/assessments'], { relativeTo: this.route });
      this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
    }
  }
}
